import React, { useState, useEffect, useRef, memo, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  Card,
  CardContent,
  Tooltip,
  Menu,
  MenuItem,
  InputBase,
  TextField,
  Avatar,
  alpha,
  useTheme,
  ListItemIcon,
  Skeleton,
  Chip,
  Fade,
  Zoom,
  Badge,
  Snackbar
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  VideoLibrary as VideoIcon,
  ThumbUp as ThumbUpIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
  Reply as ReplyIcon,
  Edit as EditIcon,
  Send as SendIcon,
  Close as CloseIcon,
  InsertComment as CommentIcon,
  CloudDownload as DownloadIcon,
  AccessTime as TimeIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon
} from '@mui/icons-material';
import ReactPlayer from 'react-player';
import { formatDistanceToNow, format } from 'date-fns';
import { FixedSizeList as VirtualList } from 'react-window';
import { usePermissions } from '../contexts/PermissionsContext';
import { API_URL } from '../config';

const ACCEPTED_VIDEO_TYPES = '.mp4,.mov,.avi,.wmv,.mkv';

const Comment = memo(({ comment, onReply, onEdit, onDelete, onLike, currentUserId }) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(comment.content);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [isReplying, setIsReplying] = useState(false);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    setIsEditing(true);
  };

  const handleDelete = () => {
    handleMenuClose();
    onDelete(comment._id);
  };

  const handleSaveEdit = () => {
    onEdit(comment._id, editedContent);
    setIsEditing(false);
  };

  const handleReply = async () => {
    setIsReplying(true);
    onReply(comment._id, replyContent);
    setReplyContent('');
    setShowReplyForm(false);
    setIsReplying(false);
  };

  const isLiked = comment.likes.includes(currentUserId);
  const isOwnComment = comment.userId === currentUserId;
  const formattedDate = useMemo(() => {
    return formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true });
  }, [comment.createdAt]);

  return (
    <Box sx={{ 
      mb: 2,
      display: 'flex',
      gap: 2,
      alignItems: 'flex-start'
    }}>
      <Avatar 
        sx={{ 
          width: 32, 
          height: 32,
          bgcolor: theme.palette.primary.main,
          fontSize: '0.875rem'
        }}
      >
        {comment.userName[0].toUpperCase()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="body2" component="span" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
            @{comment.userName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {formattedDate}
          </Typography>
          {comment.updatedAt !== comment.createdAt && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
              (edited)
            </Typography>
          )}
          {isOwnComment && (
            <>
              <IconButton
                size="small"
                onClick={handleMenuClick}
                sx={{ 
                  ml: 'auto',
                  color: theme.palette.text.secondary,
                  p: 0.5,
                  '&:hover': {
                    color: theme.palette.text.primary,
                    backgroundColor: alpha(theme.palette.text.primary, 0.1)
                  }
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  elevation: 2,
                  sx: {
                    borderRadius: 1.5,
                    minWidth: 120
                  }
                }}
              >
                <MenuItem onClick={handleEdit} sx={{ py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <EditIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
                  </ListItemIcon>
                  <Typography variant="body2">Edit</Typography>
                </MenuItem>
                <MenuItem onClick={handleDelete} sx={{ py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <DeleteIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
                  </ListItemIcon>
                  <Typography variant="body2" color="error.main">Delete</Typography>
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>

        {isEditing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
            <TextField
              fullWidth
              multiline
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              size="small"
              variant="standard"
              InputProps={{
                disableUnderline: true
              }}
              sx={{
                '& .MuiInputBase-root': {
                  py: 0.5,
                  px: 1,
                  borderRadius: '4px',
                  backgroundColor: (theme) => theme.palette.mode === 'dark' 
                    ? 'rgba(255,255,255,0.05)' 
                    : 'rgba(0,0,0,0.03)',
                }
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, alignSelf: 'flex-end' }}>
              <Button 
                onClick={() => setIsEditing(false)} 
                size="small"
                sx={{
                  borderRadius: '18px',
                  textTransform: 'none',
                  px: 2,
                  fontSize: '0.8125rem'
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveEdit} 
                variant="contained" 
                size="small"
                sx={{
                  borderRadius: '18px',
                  textTransform: 'none',
                  px: 2,
                  fontSize: '0.8125rem'
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        ) : (
          <Typography 
            variant="body2" 
            sx={{ 
              lineHeight: 1.5,
              color: theme.palette.text.primary,
              wordBreak: 'break-word',
              fontSize: '0.875rem'
            }}
          >
            {comment.content}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => onLike(comment._id)}
            sx={{ 
              borderRadius: 1,
              p: 0.5,
              color: isLiked ? theme.palette.primary.main : theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1)
              }
            }}
          >
            {isLiked ? <ThumbUpIcon sx={{ fontSize: '1rem' }} /> : <ThumbUpOutlinedIcon sx={{ fontSize: '1rem' }} />}
            {comment.likes.length > 0 && (
              <Typography variant="caption" sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                {comment.likes.length}
              </Typography>
            )}
          </IconButton>
          {(!comment.parentId || currentUserId !== comment.userId) && (
            <Button
              size="small"
              onClick={() => setShowReplyForm(!showReplyForm)}
              sx={{ 
                borderRadius: 1,
                textTransform: 'none',
                px: 1,
                py: 0.5,
                fontSize: '0.75rem',
                fontWeight: 500,
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.text.primary, 0.05)
                }
              }}
            >
              Reply
            </Button>
          )}
        </Box>

        {showReplyForm && (
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            mt: 2, 
            mb: 1,
            alignItems: 'flex-start'
          }}>
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32,
                bgcolor: theme.palette.primary.main,
                fontSize: '0.875rem'
              }}
            >
              {comment.userName[0].toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                placeholder="Add a reply..."
                variant="standard"
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                disabled={isReplying}
                InputProps={{
                  disableUnderline: true
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    py: 0.75,
                    px: 1.5,
                    borderRadius: '18px',
                    backgroundColor: (theme) => theme.palette.mode === 'dark' 
                      ? 'rgba(255,255,255,0.05)' 
                      : 'rgba(0,0,0,0.03)',
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.08)' 
                        : 'rgba(0,0,0,0.05)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: (theme) => theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.1)' 
                        : 'rgba(0,0,0,0.05)',
                      borderColor: theme.palette.primary.main,
                    }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: theme.palette.text.secondary,
                    opacity: 0.7
                  }
                }}
              />
              {replyContent.trim() && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                  <Button
                    size="small"
                    onClick={() => setShowReplyForm(false)}
                    disabled={isReplying}
                    sx={{
                      borderRadius: '18px',
                      textTransform: 'none',
                      px: 2,
                      fontSize: '0.8125rem'
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleReply}
                    size="small"
                    disabled={!replyContent.trim() || isReplying}
                    sx={{
                      borderRadius: '18px',
                      textTransform: 'none',
                      px: 2,
                      fontSize: '0.8125rem'
                    }}
                  >
                    {isReplying ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.5 }} /> : null}
                    Reply
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {comment.replies && comment.replies.length > 0 && (
        <Box sx={{ ml: 5, mt: 1, width: '100%' }}>
          {comment.replies.map((reply) => (
            <Box key={reply._id} sx={{ mb: 1 }}>
              <Comment
                comment={reply}
                onReply={onReply}
                onEdit={onEdit}
                onDelete={onDelete}
                onLike={onLike}
                currentUserId={currentUserId}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
});

const CommentSection = memo(({ dailyId }) => {
  const theme = useTheme();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const currentUserId = localStorage.getItem('userId');
  const [currentUser, setCurrentUser] = useState(null);
  const [error, setError] = useState(null);
  const [commentCount, setCommentCount] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        setCurrentUser({
          id: decoded.id,
          name: decoded.name,
          email: decoded.email
        });
      } catch (error) {
        console.error('Error decoding token:', error);
        setError('Authentication error. Please try logging in again.');
      }
    }
  }, []);

  const fetchComments = async () => {
    if (!dailyId) return;
    
    try {
      setIsLoading(true);
      console.log('Fetching comments for dailyId:', dailyId);
      const response = await fetch(`${API_URL}/api/v1/comments/daily/${dailyId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch comments');
      }
      const data = await response.json();
      console.log('Fetched comments:', data);
      setComments(data);
      setCommentCount(data.length);
      setError(null);
    } catch (error) {
      console.error('Error fetching comments:', error);
      setError('Failed to load comments. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (dailyId) {
      fetchComments();
    }
  }, [dailyId]);

  const handleAddComment = async () => {
    if (!newComment.trim() || !currentUser) return;

    try {
      setIsSubmitting(true);
      setError(null);
      const response = await fetch(`${API_URL}/api/v1/comments/daily/${dailyId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          content: newComment,
          userName: currentUser.name
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add comment');
      }

      const data = await response.json();
      console.log('Added comment:', data);
      setComments([data, ...comments]);
      setCommentCount(prev => prev + 1);
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
      setError('Failed to add comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReply = async (parentId, content) => {
    try {
      const response = await fetch(`${API_URL}/api/v1/comments/daily/${dailyId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ content, parentId })
      });

      if (!response.ok) {
        throw new Error('Failed to add reply');
      }

      const updatedComment = await response.json();
      setComments(comments.map(c => 
        c._id === parentId ? { ...c, replies: [...(c.replies || []), updatedComment] } : c
      ));
    } catch (error) {
      console.error('Error adding reply:', error);
      setError('Failed to add reply. Please try again.');
    }
  };

  const handleEdit = async (commentId, content) => {
    try {
      const response = await fetch(`${API_URL}/api/v1/comments/${commentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ content })
      });

      if (!response.ok) {
        throw new Error('Failed to update comment');
      }

      const updatedComment = await response.json();
      setComments(comments.map(c => 
        c._id === commentId ? { ...c, content: updatedComment.content, updatedAt: updatedComment.updatedAt } : c
      ));
    } catch (error) {
      console.error('Error updating comment:', error);
      setError('Failed to update comment. Please try again.');
    }
  };

  const handleDelete = async (commentId) => {
    try {
      const response = await fetch(`${API_URL}/api/v1/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete comment');
      }

      setComments(comments.filter(c => c._id !== commentId));
      setCommentCount(prev => prev - 1);
    } catch (error) {
      console.error('Error deleting comment:', error);
      setError('Failed to delete comment. Please try again.');
    }
  };

  const handleLike = async (commentId) => {
    try {
      const response = await fetch(`${API_URL}/api/v1/comments/${commentId}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to like comment');
      }

      const updatedComment = await response.json();
      setComments(comments.map(c => 
        c._id === commentId ? { ...c, likes: updatedComment.likes } : c
      ));
    } catch (error) {
      console.error('Error toggling like:', error);
      setError('Failed to like comment. Please try again.');
    }
  };

  const Row = ({ index, style }) => (
    <Box style={style} sx={{ mb: 2, pb: 2 }}>
      <Comment
        comment={comments[index]}
        onReply={handleReply}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onLike={handleLike}
        currentUserId={currentUser?.id}
      />
    </Box>
  );

  return (
    <Box sx={{ 
      mt: 2, 
      backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#000' : '#fff',
      width: '100%',
      maxWidth: { xs: '100%', md: 960, xl: 1280 }
    }}>
      {/* YouTube-style header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        pb: 2,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`
      }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            fontSize: '1rem'
          }}
        >
          {commentCount} Comments
        </Typography>
      </Box>

      {/* YouTube-style add comment input */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        py: 2,
      }}>
        <Avatar 
          sx={{ 
            width: 40, 
            height: 40,
            bgcolor: theme.palette.primary.main,
            fontSize: '1rem'
          }}
        >
          {currentUser?.name?.charAt(0) || 'U'}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <TextField
            fullWidth
            placeholder="Add a comment..."
            variant="standard"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            disabled={isSubmitting || !currentUser}
            InputProps={{
              disableUnderline: true
            }}
            sx={{
              '& .MuiInputBase-root': {
                py: 1,
                px: 1.5,
                borderRadius: '18px',
                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.05)' 
                  : 'rgba(0,0,0,0.03)',
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' 
                    ? 'rgba(255,255,255,0.08)' 
                    : 'rgba(0,0,0,0.05)',
                },
                '&.Mui-focused': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' 
                    ? 'rgba(255,255,255,0.1)' 
                    : 'rgba(0,0,0,0.05)',
                  borderColor: theme.palette.primary.main,
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
              },
              '& .MuiInputBase-input::placeholder': {
                color: theme.palette.text.secondary,
                opacity: 0.7
              }
            }}
          />
          {newComment.trim() && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              <Button
                size="small"
                onClick={() => setNewComment('')}
                sx={{ 
                  borderRadius: '18px',
                  textTransform: 'none',
                  px: 2,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.text.primary, 0.05)
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={!newComment.trim() || isSubmitting || !currentUser}
                onClick={handleAddComment}
                startIcon={isSubmitting ? <CircularProgress size={14} color="inherit" /> : null}
                sx={{ 
                  borderRadius: '18px',
                  textTransform: 'none',
                  px: 2,
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
              >
                Comment
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[...Array(3)].map((_, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 2 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="100%" height={60} />
                <Skeleton variant="text" width="40%" height={24} />
              </Box>
            </Box>
          ))}
        </Box>
      ) : error ? (
        <Alert 
          severity="error" 
          sx={{ 
            m: 2,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}
        >
          {error}
        </Alert>
      ) : comments.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '200px',
          p: 4
        }}>
          <CommentIcon sx={{ fontSize: 48, color: alpha(theme.palette.text.secondary, 0.5), mb: 2 }} />
          <Typography variant="body1" color="text.secondary" align="center">
            No comments yet. Be the first to comment!
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          <VirtualList
            height={380}
            itemCount={comments.length}
            itemSize={180}
            width={'100%'}
            overscanCount={3}
          >
            {Row}
          </VirtualList>
        </Box>
      )}
    </Box>
  );
});

const VideoPlayer = memo(({ selectedVideo }) => {
  const theme = useTheme();
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [played, setPlayed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);
  const [isProgressHovered, setIsProgressHovered] = useState(false);
  const playerContainerRef = useRef(null);
  const playerRef = useRef(null);
  const progressRef = useRef(null);
  const isSeekingRef = useRef(false);

  useEffect(() => {
    const handler = () => {
      const inFs = !!document.fullscreenElement;
      setIsFullscreen(inFs);
    };
    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler);
    document.addEventListener('msfullscreenchange', handler);
    return () => {
      document.removeEventListener('fullscreenchange', handler);
      document.removeEventListener('webkitfullscreenchange', handler);
      document.removeEventListener('msfullscreenchange', handler);
    };
  }, []);

  // Global mouse events for seeking
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isSeekingRef.current || !progressRef.current) return;
      const rect = progressRef.current.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      const clampedPos = Math.max(0, Math.min(1, pos));
      setPlayed(clampedPos);
    };

    const handleMouseUp = () => {
      if (isSeekingRef.current) {
        isSeekingRef.current = false;
        setIsSeeking(false);
        playerRef.current?.seekTo(played, 'fraction');
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove);
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [played]);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = selectedVideo.url;
    link.download = selectedVideo.originalName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "0:00";
    const date = new Date(seconds * 1000);
    const hh = date.getUTCHours();
    const mm = date.getUTCMinutes();
    const ss = date.getUTCSeconds().toString().padStart(2, "0");
    if (hh) {
      return `${hh}:${mm.toString().padStart(2, "0")}:${ss}`;
    }
    return `${mm}:${ss}`;
  };

  const handleProgress = (state) => {
    if (!isSeekingRef.current) {
      setPlayed(state.played);
    }
  };

  const handleSeekMouseDown = (e) => {
    e.preventDefault();
    isSeekingRef.current = true;
    setIsSeeking(true);
  };

  const handleProgressDrag = (e) => {
    if (!isSeekingRef.current || !progressRef.current) return;
    const rect = progressRef.current.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const clampedPos = Math.max(0, Math.min(1, pos));
    setPlayed(clampedPos);
  };

  const handleProgressDragEnd = () => {
    if (isSeekingRef.current) {
      isSeekingRef.current = false;
      setIsSeeking(false);
      playerRef.current?.seekTo(played, 'fraction');
    }
  };

  const handleProgressClick = (e) => {
    if (isSeekingRef.current) return;
    if (!progressRef.current) return;
    const rect = progressRef.current.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const clampedPos = Math.max(0, Math.min(1, pos));
    setPlayed(clampedPos);
    playerRef.current?.seekTo(clampedPos, 'fraction');
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      if (playerContainerRef.current.requestFullscreen) {
        playerContainerRef.current.requestFullscreen();
      } else if (playerContainerRef.current.webkitRequestFullscreen) {
        playerContainerRef.current.webkitRequestFullscreen();
      } else if (playerContainerRef.current.msRequestFullscreen) {
        playerContainerRef.current.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  const toggleMute = () => {
    setMuted(!muted);
  };

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{
        position: 'relative',
        top: 0,
        backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#000' : '#fff',
        borderRadius: 1,
        mb: 2,
        overflow: 'hidden',
        width: '100%',
        maxWidth: { xs: '100%', md: 960, xl: 1280 },
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`
      }}>
        <Box sx={{ 
          p: 2, 
          borderBottom: 1, 
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box>
            <Typography 
              variant="h6" 
              noWrap 
              sx={{ 
                fontWeight: 600,
                maxWidth: '80ch',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {selectedVideo.originalName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <TimeIcon fontSize="inherit" />
                {formatDistanceToNow(new Date(selectedVideo.uploadDate), { addSuffix: true })}
              </Typography>
              {selectedVideo.fileSize && (
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  {(selectedVideo.fileSize / (1024 * 1024)).toFixed(1)} MB
                </Typography>
              )}
              {selectedVideo.uploadedBy && (
                <Chip 
                  size="small" 
                  label={selectedVideo.uploadedBy.name || 'Unknown'}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </Box>
          <Tooltip title="Download video">
            <IconButton onClick={handleDownload} size="small">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Box 
          ref={playerContainerRef}
          sx={{ 
            width: '100%',
            aspectRatio: '16 / 9',
            backgroundColor: 'black',
            position: 'relative',
            borderTop: (theme) => theme.palette.mode === 'dark'
              ? `1px solid ${alpha(theme.palette.common.white, 0.08)}`
              : `1px solid ${alpha(theme.palette.divider, 0.06)}`,
            boxShadow: (theme) => theme.palette.mode === 'dark'
              ? `inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.06)}`
              : 'none',
            '&:hover': {
              '& .player-controls': {
                opacity: 1,
              }
            },
            // Ensure the underlying <video> scales to container without cropping after exiting fullscreen
            '& video': {
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            },
            ...(isFullscreen && {
              height: '100vh',
              width: '100vw',
              aspectRatio: 'auto',
              '& .react-player': {
                width: '100%',
                height: '100%'
              }
            })
          }}
          onMouseEnter={() => setShowControls(true)}
          onMouseLeave={() => setShowControls(false)}
        >
          <ReactPlayer
            ref={playerRef}
            url={selectedVideo.url}
            className="react-player"
            width="100%"
            height="100%"
            playing={isPlaying}
            volume={volume}
            muted={muted}
            controls={false}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onDuration={setDuration}
            onProgress={handleProgress}
            progressInterval={500}
            config={{
              file: {
                attributes: {
                  controlsList: 'nodownload',
                  disablePictureInPicture: true
                }
              }
            }}
          />
          
          <Fade in={showControls || !isPlaying}>
            <Box 
              className="player-controls"
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                padding: 2,
                background: (theme) => theme.palette.mode === 'dark'
                  ? 'linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0))'
                  : 'linear-gradient(to top, rgba(0,0,0,0.35), rgba(0,0,0,0))',
                borderTop: (theme) => theme.palette.mode === 'dark'
                  ? `1px solid ${alpha(theme.palette.common.white, 0.08)}`
                  : `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                opacity: showControls || !isPlaying ? 1 : 0,
                transition: 'opacity 0.3s ease'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                <Typography variant="caption" sx={{ minWidth: 45 }}>
                  {formatTime(duration * played)}
                </Typography>
                <Box 
                  ref={progressRef}
                  sx={{ flex: 1, cursor: 'pointer', position: 'relative' }}
                  onClick={handleProgressClick}
                  onMouseDown={handleSeekMouseDown}
                  onMouseEnter={() => setIsProgressHovered(true)}
                  onMouseLeave={() => !isSeekingRef.current && setIsProgressHovered(false)}
                >
                  {/* Progress track */}
                  <Box sx={{
                    height: isProgressHovered || isSeeking ? 8 : 6,
                    borderRadius: 3,
                    backgroundColor: alpha(theme.palette.common.white, 0.2),
                    transition: 'height 0.15s ease',
                    overflow: 'hidden'
                  }}>
                    {/* Progress fill */}
                    <Box sx={{
                      height: '100%',
                      width: `${played * 100}%`,
                      backgroundColor: theme.palette.primary.main,
                      transition: isSeeking ? 'none' : 'width 0.1s linear'
                    }} />
                  </Box>
                  {/* Scrubber handle - hidden until hover or seeking */}
                  <Box
                    onMouseDown={handleSeekMouseDown}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: `${played * 100}%`,
                      transform: 'translate(-50%, -50%)',
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.main,
                      border: '2px solid white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                      cursor: isSeeking ? 'grabbing' : 'grab',
                      opacity: isProgressHovered || isSeeking ? 1 : 0,
                      transition: 'opacity 0.15s ease, transform 0.15s ease',
                      '&:hover': {
                        transform: 'translate(-50%, -50%) scale(1.15)'
                      }
                    }}
                  />
                </Box>
                <Typography variant="caption" sx={{ minWidth: 45 }}>
                  {formatTime(duration)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
                    <IconButton 
                      onClick={() => setIsPlaying(!isPlaying)}
                      sx={{ 
                        color: '#fff',
                        backgroundColor: (theme) => alpha('#000', theme.palette.mode === 'dark' ? 0.3 : 0.2),
                        '&:hover': { backgroundColor: (theme) => alpha('#000', theme.palette.mode === 'dark' ? 0.45 : 0.35) },
                        borderRadius: 2,
                        boxShadow: (theme) => theme.palette.mode === 'dark' ? `0 2px 6px ${alpha('#000', 0.6)}` : '0 2px 6px rgba(0,0,0,0.2)'
                      }}
                    >
                      {isPlaying ? <PauseIcon /> : <PlayIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={muted ? 'Unmute' : 'Mute'}>
                    <IconButton 
                      onClick={toggleMute}
                      sx={{ 
                        color: '#fff',
                        backgroundColor: (theme) => alpha('#000', theme.palette.mode === 'dark' ? 0.3 : 0.2),
                        '&:hover': { backgroundColor: (theme) => alpha('#000', theme.palette.mode === 'dark' ? 0.45 : 0.35) },
                        borderRadius: 2,
                        boxShadow: (theme) => theme.palette.mode === 'dark' ? `0 2px 6px ${alpha('#000', 0.6)}` : '0 2px 6px rgba(0,0,0,0.2)'
                      }}
                    >
                      {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}>
                    <IconButton 
                      onClick={toggleFullscreen}
                      sx={{ 
                        color: '#fff',
                        backgroundColor: (theme) => alpha('#000', theme.palette.mode === 'dark' ? 0.3 : 0.2),
                        '&:hover': { backgroundColor: (theme) => alpha('#000', theme.palette.mode === 'dark' ? 0.45 : 0.35) },
                        borderRadius: 2,
                        boxShadow: (theme) => theme.palette.mode === 'dark' ? `0 2px 6px ${alpha('#000', 0.6)}` : '0 2px 6px rgba(0,0,0,0.2)'
                      }}
                    >
                      {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          </Fade>
        </Box>
      </Box>
    </Box>
  );
});

const Dailies = ({ projectId }) => {
  const theme = useTheme();
  // Permissions gating
  const { loading: permsLoading, can } = usePermissions();
  const hasViewPerm = can('dailies.view');
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const fileInputRef = useRef(null);
  const [showNotification, setShowNotification] = useState(false);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('Selected file:', {
      name: file.name,
      type: file.type,
      size: file.size
    });

    try {
      // Validate the file first
      validateVideoFile(file);

      setIsUploading(true);
      setError(null);
      setUploadProgress(0);
      setSuccessMessage('');

      const formData = new FormData();
      formData.append('video', file); // Make sure this matches the field name expected by multer

      console.log('Uploading file to:', `${API_URL}/api/v1/dailies/${projectId}/upload`);

      const response = await fetch(`${API_URL}/api/v1/dailies/${projectId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      console.log('Upload response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload failed with response:', errorText);

        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || 'Upload failed';
        } catch (e) {
          errorMessage = errorText || 'Upload failed';
        }

        throw new Error(errorMessage);
      }

      let result = null;
      try {
        const contentType = response.headers.get('content-type') || '';
        const text = await response.text();
        if (text && contentType.includes('application/json')) {
          result = JSON.parse(text);
        } else if (!text) {
          console.log('Upload succeeded with empty response body');
        } else {
          console.log('Upload succeeded with non-JSON body:', text);
        }
      } catch (e) {
        console.warn('Upload succeeded but parsing response failed:', e);
      }
      console.log('Upload result:', result);

      if (result) {
        // Normalize the newly uploaded video to match transformed dailies
        const newVideo = {
          ...result,
          url: result.url && result.url.startsWith('http')
            ? result.url
            : `${API_URL}${result.url || `/uploads/${result.path}`}`,
          displayName: result.originalName || result.filename,
          formattedSize: result.fileSize
            ? (result.fileSize / (1024 * 1024)).toFixed(1) + ' MB'
            : undefined,
          formattedDate: result.uploadDate
            ? formatDistanceToNow(new Date(result.uploadDate), { addSuffix: true })
            : undefined,
          exactDate: result.uploadDate
            ? format(new Date(result.uploadDate), 'PPpp')
            : undefined
        };

        setVideos(prev => [newVideo, ...prev]);

        // If nothing is selected yet, auto-select the newly uploaded video
        if (!selectedVideo) {
          setSelectedVideo(newVideo);
        }
      }

      setSuccessMessage('Video uploaded successfully!');
      setUploadProgress(100);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Upload error details:', error);
      setError(error.message || 'Failed to upload video');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const validateVideoFile = (file) => {
    const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-ms-wmv', 'video/x-matroska'];
    const maxSize = 1024 * 1024 * 500; // 500MB

    if (!validTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a supported video format (mp4, mov, avi, wmv, mkv).');
    }

    if (file.size > maxSize) {
      throw new Error('File is too large. Maximum size is 500MB.');
    }

    return true;
  };

  // Fetch videos when permitted
  useEffect(() => {
    if (!projectId) return;
    if (permsLoading) return;
    if (!hasViewPerm) return;
    fetchVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, permsLoading, hasViewPerm]);

  const fetchVideos = async () => {
    if (!projectId) return;

    try {
      setIsLoading(true);
      console.log('Fetching videos for project:', projectId);

      const response = await fetch(`${API_URL}/api/v1/dailies/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        let message = 'Failed to fetch videos';
        try {
          const errorData = await response.json();
          if (errorData && (errorData.message || errorData.error)) {
            message = errorData.message || errorData.error;
          }
        } catch (e) {
          const text = await response.text();
          console.error('Non-JSON error response while fetching videos:', text);
        }
        throw new Error(message);
      }

      const data = await response.json();
      console.log('Fetched dailies:', data);

      const transformedData = data.map(daily => ({
        ...daily,
        url: `${API_URL}/uploads/${daily.path}`,
        displayName: daily.originalName || daily.filename,
        formattedSize: (daily.fileSize / (1024 * 1024)).toFixed(1) + ' MB',
        formattedDate: formatDistanceToNow(new Date(daily.uploadDate), { addSuffix: true }),
        exactDate: format(new Date(daily.uploadDate), 'PPpp')
      }));

      console.log('Transformed dailies:', transformedData);
      setVideos(transformedData);
      setError(null);

      if (transformedData.length > 0 && !selectedVideo) {
        setSelectedVideo(transformedData[0]);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
      setError(error.message || 'Failed to load videos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!videoId) {
      console.error('Invalid video ID');
      setError('Cannot delete video: Invalid video ID');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/v1/dailies/${projectId}/${videoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete video');
      }

      // Remove the deleted video from state
      setVideos(prevVideos => prevVideos.filter(video => video._id !== videoId));
      if (selectedVideo?._id === videoId) {
        setSelectedVideo(null);
      }
      setError(null);
    } catch (error) {
      console.error('Error deleting video:', error);
      setError(error.message || 'Failed to delete video');
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSortMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSortMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSortChange = (sortType) => {
    setSortBy(sortType);
    handleSortMenuClose();
  };

  const handleDeleteClick = (video) => {
    if (!video || !video._id) {
      console.error('Invalid video object:', video);
      setError('Cannot delete video: Invalid video data');
      return;
    }
    setVideoToDelete(video);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!videoToDelete || !videoToDelete._id) {
      console.error('No video selected for deletion');
      setError('Cannot delete video: No video selected');
      setDeleteDialogOpen(false);
      setVideoToDelete(null);
      return;
    }

    try {
      await handleDeleteVideo(videoToDelete._id);
      setDeleteDialogOpen(false);
      setVideoToDelete(null);
      setSuccessMessage('Video deleted successfully');
    } catch (error) {
      console.error('Error in handleConfirmDelete:', error);
      setError(error.message || 'Failed to delete video');
      setDeleteDialogOpen(false);
      setVideoToDelete(null);
    }
  };

  const filteredAndSortedVideos = videos
    .filter(video => 
      video.originalName.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.originalName.localeCompare(b.originalName);
        case 'size':
          return b.fileSize - a.fileSize;
        case 'date':
        default:
          return new Date(b.uploadDate) - new Date(a.uploadDate);
      }
    });

  return (
    <Box sx={{ 
      minHeight: '90vh', 
      display: 'flex', 
      gap: 3,
      p: 3,
      pb: 4,
      bgcolor: (theme) => theme.palette.mode === 'dark' ? '#000' : '#fff',
      width: '100%'
    }}>
      {/* Left Panel - Video List */}
  <Paper sx={{ 
    width: 320,
    alignSelf: 'flex-start', 
    position: 'sticky',
    top: 0,
    display: 'flex', 
    flexDirection: 'column',
    borderRadius: 1,
    backgroundColor: (theme) => theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
    overflow: 'hidden'
  }}>
        <Box sx={{ 
          p: 2, 
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#000' : 'transparent'
        }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 600,
              color: (theme) => theme.palette.text.primary
            }}
          >
            Daily Footage
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: (theme) => theme.palette.text.secondary,
              mt: 0.5
            }}
          >
            Upload and review daily video footage
          </Typography>
          
          <Button
            variant="contained"
            startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
            component="label"
            fullWidth
            disabled={isUploading}
            sx={{ 
              mt: 1.5,
              mb: 2,
              borderRadius: 1,
              textTransform: 'none'
            }}
          >
            {isUploading ? 'Uploading...' : 'Upload Video'}
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept={ACCEPTED_VIDEO_TYPES}
              onChange={handleFileSelect}
            />
          </Button>

          {isUploading && (
            <Box sx={{ width: '100%', mb: 2 }}>
              <LinearProgress 
                variant="determinate" 
                value={uploadProgress} 
                sx={{
                  height: 6,
                  borderRadius: 3,
                  mb: 1,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '& .MuiLinearProgress-bar': {
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary" align="center" display="block">
                Uploading... {uploadProgress}%
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Paper
              elevation={0}
              sx={{
                p: '2px 4px',
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                backgroundColor: (theme) => theme.palette.background.paper,
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                boxShadow: 'none'
              }}
            >
              <InputBase
                sx={{ 
                  ml: 1.5, 
                  flex: 1,
                  fontSize: '0.9rem',
                  color: (theme) => theme.palette.text.primary
                }}
                placeholder="Search videos..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <IconButton sx={{ p: '8px' }}>
                <SearchIcon fontSize="small" />
              </IconButton>
            </Paper>
            <Tooltip title="Sort videos">
              <IconButton 
                onClick={handleSortMenuClick}
                sx={{
                  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                  borderRadius: 1
                }}
              >
                <SortIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {isLoading ? (
          <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[...Array(5)].map((_, i) => (
              <Box key={i}>
                <Skeleton variant="rectangular" width="100%" height={20} sx={{ mb: 1, borderRadius: 1 }} />
                <Skeleton variant="rectangular" width="60%" height={16} sx={{ borderRadius: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Skeleton variant="rectangular" width="40%" height={12} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="rectangular" width="20%" height={12} sx={{ borderRadius: 1 }} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : filteredAndSortedVideos.length === 0 ? (
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            p: 4,
            m: 2,
            borderRadius: 1,
            backgroundColor: (theme) => theme.palette.background.paper,
            border: `1px dashed ${alpha(theme.palette.divider, 0.3)}`,
            color: (theme) => theme.palette.text.secondary
          }}>
            <VideoIcon sx={{ 
              fontSize: 50, 
              mb: 2, 
              opacity: 0.7,
              color: theme.palette.mode === 'dark' 
                ? alpha(theme.palette.primary.main, 0.7) 
                : theme.palette.primary.main,
              filter: theme.palette.mode === 'dark' 
                ? 'drop-shadow(0 0 8px rgba(33, 150, 243, 0.3))' 
                : 'none'
            }} />
            {searchTerm ? (
              <Typography variant="body1" align="center" sx={{ fontWeight: 500 }}>
                No videos match your search
              </Typography>
            ) : (
              <>
                <Typography variant="body1" align="center" sx={{ fontWeight: 500, mb: 1 }}>
                  No videos available
                </Typography>
                <Typography variant="body2" align="center" color="text.secondary">
                  Upload your first video to get started
                </Typography>
              </>
            )}
          </Box>
        ) : (
          <Box sx={{ p: 2.5, display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: 2, backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#000' : 'transparent' }}>
            {filteredAndSortedVideos.map((video) => (
              <Card 
                key={video._id}
                elevation={0}
                onClick={() => setSelectedVideo(video)}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  borderRadius: 1,
                  overflow: 'visible',
                  position: 'relative',
                  backgroundColor: (theme) => theme.palette.background.paper,
                  border: selectedVideo?._id === video._id 
                    ? `1px solid ${theme.palette.primary.main}` 
                    : `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                  '&:hover': {
                    borderColor: alpha(theme.palette.primary.main, 0.3)
                  }
                }}
              >
                {selectedVideo?._id === video._id && (
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: -10, 
                      left: 16, 
                      backgroundColor: (theme) => theme.palette.primary.main,
                      color: 'white',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      zIndex: 1
                    }}
                  >
                    Now Playing
                  </Box>
                )}
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VideoIcon color={selectedVideo?._id === video._id ? "primary" : "action"} />
                      <Typography 
                        variant="subtitle1" 
                        noWrap 
                        sx={{ 
                          fontWeight: selectedVideo?._id === video._id ? 600 : 500,
                          color: selectedVideo?._id === video._id ? theme.palette.primary.main : theme.palette.text.primary,
                          maxWidth: '180px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {video.originalName}
                      </Typography>
                    </Box>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(video);
                        }}
                        sx={{
                          color: theme.palette.text.secondary,
                          '&:hover': {
                            color: theme.palette.error.main,
                            backgroundColor: alpha(theme.palette.error.main, 0.1)
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Avatar 
                        sx={{ width: 16, height: 16, fontSize: '0.6rem', bgcolor: theme.palette.primary.light }}
                      >
                        {(video.uploadedBy?.name || 'U')[0].toUpperCase()}
                      </Avatar>
                      {video.uploadedBy?.name || 'Unknown'}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Tooltip title={video.exactDate || ''}>
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          <TimeIcon fontSize="inherit" />
                          {video.formattedDate}
                        </Typography>
                      </Tooltip>
                      <Chip 
                        label={video.formattedSize} 
                        size="small" 
                        variant="outlined" 
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Paper>

      {/* Right Panel - Video Player */}
      <Box sx={{ 
        flex: 2, 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {selectedVideo ? (
          <>
            <VideoPlayer selectedVideo={selectedVideo} />
            <CommentSection dailyId={selectedVideo._id} />
          </>
        ) : (
          <Box
            sx={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: 4
            }}
          >
            <Box sx={{ 
              width: 120, 
              height: 120, 
              borderRadius: '50%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              background: theme.palette.mode === 'dark'
                ? '#000'
                : `radial-gradient(circle, ${alpha('#2196F3', 0.2)} 0%, ${alpha('#2196F3', 0.05)} 70%)`,
              mb: 3,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: '50%',
                border: theme.palette.mode === 'dark'
                  ? `1px solid ${alpha('#2196F3', 0.3)}`
                  : `1px solid ${alpha('#2196F3', 0.2)}`,
                boxShadow: theme.palette.mode === 'dark'
                  ? `0 0 20px ${alpha('#2196F3', 0.3)}`
                  : `0 0 20px ${alpha('#2196F3', 0.15)}`
              }
            }}>
              <VideoIcon sx={{ 
                fontSize: 60, 
                color: theme.palette.mode === 'dark'
                  ? '#2196F3'
                  : '#2196F3',
                filter: theme.palette.mode === 'dark'
                  ? 'drop-shadow(0 0 8px rgba(33, 150, 243, 0.4))'
                  : 'drop-shadow(0 0 8px rgba(33, 150, 243, 0.2))'
              }} />
            </Box>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 1, 
                fontWeight: 600, 
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(90deg, #2196F3, #2196F3)'
                  : 'linear-gradient(90deg, #2196F3, #2196F3)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textAlign: 'center'
              }}
            >
              Select a video to play
            </Typography>
            <Typography variant="body1" align="center" sx={{ 
              maxWidth: 450, 
              mb: 3, 
              color: alpha(theme.palette.text.primary, 0.7),
              fontWeight: 400
            }}>
              Choose a video from the list or upload a new one to get started with your dailies review
            </Typography>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              component="label"
              sx={{ 
                borderRadius: 1,
                py: 1.2,
                px: 3,
                textTransform: 'none',
                background: theme.palette.mode === 'dark'
                  ? `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)`
                  : `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: theme.palette.mode === 'dark' 
                  ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}` 
                  : `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                border: theme.palette.mode === 'dark'
                  ? `1px solid ${alpha(theme.palette.primary.light, 0.2)}`
                  : 'none',
                fontWeight: 500,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: theme.palette.mode === 'dark' 
                    ? `0 6px 16px ${alpha(theme.palette.primary.main, 0.6)}` 
                    : `0 6px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                  transform: 'translateY(-2px)'
                },
                '&:active': {
                  boxShadow: theme.palette.mode === 'dark' 
                    ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}` 
                    : `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                  transform: 'translateY(1px)'
                }
              }}
            >
              Upload Video
              <input
                type="file"
                hidden
                accept={ACCEPTED_VIDEO_TYPES}
                onChange={handleFileSelect}
              />
            </Button>
          </Box>
        )}
      </Box>

      {/* Sort Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleSortMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: 2,
            minWidth: 180,
            mt: 1
          }
        }}
      >
        <MenuItem 
          onClick={() => handleSortChange('date')} 
          selected={sortBy === 'date'}
          sx={{ 
            borderRadius: 1,
            mx: 0.5,
            '&.Mui-selected': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.15),
              }
            }
          }}
        >
          <ListItemIcon>
            <TimeIcon fontSize="small" color={sortBy === 'date' ? "primary" : "inherit"} />
          </ListItemIcon>
          <Typography variant="body2">Sort by Date</Typography>
        </MenuItem>
        <MenuItem 
          onClick={() => handleSortChange('name')} 
          selected={sortBy === 'name'}
          sx={{ 
            borderRadius: 1,
            mx: 0.5,
            '&.Mui-selected': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.15),
              }
            }
          }}
        >
          <ListItemIcon>
            <SortIcon fontSize="small" color={sortBy === 'name' ? "primary" : "inherit"} />
          </ListItemIcon>
          <Typography variant="body2">Sort by Name</Typography>
        </MenuItem>
        <MenuItem 
          onClick={() => handleSortChange('size')} 
          selected={sortBy === 'size'}
          sx={{ 
            borderRadius: 1,
            mx: 0.5,
            '&.Mui-selected': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.15),
              }
            }
          }}
        >
          <ListItemIcon>
            <FilterIcon fontSize="small" color={sortBy === 'size' ? "primary" : "inherit"} />
          </ListItemIcon>
          <Typography variant="body2">Sort by Size</Typography>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: (theme) => theme.palette.background.paper,
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              Confirm Delete
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pb: 2, pt: 1 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Are you sure you want to delete this video?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            "{videoToDelete?.originalName}"
          </Typography>
          <Typography variant="body2" color="error.main" sx={{ mt: 2, fontWeight: 500 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              px: 2
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            startIcon={<DeleteIcon />}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              px: 2,
              boxShadow: 2
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setError(null)}
          sx={{ 
            borderRadius: 2,
            boxShadow: theme.palette.mode === 'dark' 
              ? `0 4px 20px ${alpha(theme.palette.common.black, 0.3)}` 
              : '0 4px 20px rgba(0,0,0,0.15)',
            minWidth: '280px'
          }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSuccessMessage('')}
          sx={{ 
            borderRadius: 2,
            boxShadow: theme.palette.mode === 'dark' 
              ? `0 4px 20px ${alpha(theme.palette.common.black, 0.3)}` 
              : '0 4px 20px rgba(0,0,0,0.15)',
            minWidth: '280px'
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dailies;
